<template>
  <div class="question-list-wrapper">
    <div v-if="errorMessages.length" class="question-list-alerts">
      <div v-for="(error, index) in errorMessages" :key="'error-' + index" class="alert-item alert-danger">
        <span>{{ error }}</span>
        <button class="alert-close" type="button" @click.stop.prevent="removeError(index)">&times;</button>
      </div>
    </div>
    <div v-if="successMessage" class="question-list-alerts">
      <div class="alert-item alert-success">
        <span>{{ successMessage }}</span>
        <button class="alert-close" type="button" @click.stop.prevent="successMessage = null">&times;</button>
      </div>
    </div>

    <div class="question-list-body">
      <template v-for="(question, questionIndex) in questions">
        <div v-if="question !== null && question.type === 1" :key="'q_' + questionIndex" class="question-item">
          <p class="question-title">{{ (questionIndex + 1) + '. ' + question.name }}</p>
          <multiple-select-input :allow-empty="false" :model.sync="question.selectedOption"
                                 :multiple="false"
                                 :searchable="false"
                                 :show-labels="false"
                                 :values="question.options"
                                 label="label"
                                 track-by="point"
                                 @select="setSelectedOption(question, $event)"
          />
        </div>
        <div v-if="question !== null && question.type === 2" :key="'q_' + questionIndex" class="question-item">
          <p class="question-title">{{ (questionIndex + 1) + '. ' + question.name }}</p>
          <textarea v-model="question.answerText"
                    :placeholder="$t('general.write_your_answer')"
                    class="question-textarea"
                    rows="2"></textarea>
        </div>
      </template>
    </div>

    <div class="question-list-footer">
      <button v-if="modal_name" class="btn-secondary-action" @click="closeModal">
        {{ $t("general.close") }}
      </button>
      <button v-if="!hideSubmit" class="btn-primary-action" @click="onSubmit">
        {{ $t("general.submit") }}
      </button>
    </div>
  </div>
</template>

<script>
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput.vue";
import _ from "lodash";

export default {
  components: { MultipleSelectInput },
  name: "QuestionList",
  props: {
    survey: {
      required: true,
      default: () => [],
    },
    modal_name: {
      required: true,
    },
    modal_title: {
      required: false,
    },
    hideSubmit: {
      required: false,
      default: false,
    }
  },
  data() {
    return {
      errorMessages: [],
      successMessage: null,
      questions: []
    };
  },
  methods: {
    buildQuestions() {
      const surveyQuestions = this.survey && Array.isArray(this.survey.questions) ? this.survey.questions : [];

      this.questions = surveyQuestions.map(question => {
        let options = [];
        if (question.type === 1) {
          try {
            options = JSON.parse(question.options) || [];
          } catch (e) {
            options = [];
          }
        }
        return {
          id: question.id,
          name: question.name,
          type: question.type,
          answerPoint: null,
          selectedOption: null,
          answerText: '',
          options: Array.isArray(options) ? options : []
        };
      });
    },
    setSelectedOption(question, selectedOption) {
      question.selectedOption = selectedOption || null;
      question.answerPoint = selectedOption ? selectedOption.point : null;
    },
    onSubmit() {
      this.resetMessages();
      let validated = true;
      let userAnswers = [];

      for (const question of this.questions) {
        if (question.type === 1 && question.answerPoint == null) {
          this.errorMessages.push(this.$t("question.question_required").replace(":NAME", question.name));
          validated = false;
        }
        if (question.type === 2 && (!question.answerText || question.answerText.trim().length === 0)) {
          this.errorMessages.push(this.$t("question.question_required").replace(":NAME", question.name));
          validated = false;
        }
        userAnswers.push({
          question_id: question.id,
          answer_point: question.answerPoint ?? 0,
          question_text: question.name,
          answer_text: question.answerText,
        });
      }

      if (validated) {
        this.$emit("onSubmit", { answers: userAnswers }, (data) => {
          if (!data.status) {
            if (data.code !== 422) {
              this.errorMessages.push(this.$t(data.error.message));
              return;
            }
            this.showErrors(data.error.errors);
          } else {
            this.successMessage = this.$t('survey.saved_successfully');
            setTimeout(() => this.closeModal(), 1000);
          }
        });
      }
    },
    showErrors(errors) {
      this.resetMessages();
      for (const error of Object.values(errors)) {
        if (Array.isArray(error)) {
          error.forEach(text => this.errorMessages.push(text));
        } else {
          this.errorMessages.push(error);
        }
      }
    },
    closeModal() {
      this.$emit('onClose', true);
      if (this.modal_name) this.$modal.hide(this.modal_name);
    },
    resetMessages() {
      this.errorMessages = [];
    },
    removeError(index) {
      this.errorMessages.splice(index, 1);
    },
  },
  watch: {
    survey: {
      handler() {
        this.buildQuestions();
      },
      immediate: true
    },
    errorMessages() {
      this.debouncedResetValues();
    },
  },
  created() {
    this.debouncedResetValues = _.debounce(this.resetMessages, 2500);
  },
};
</script>

<style scoped>
.question-list-wrapper {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 48px);
  max-height: calc(100vh - 48px);
  overflow: hidden;
  box-sizing: border-box;
  padding: 16px;
}

/* Alerts */
.question-list-alerts {
  flex-shrink: 0;
  margin-bottom: 12px;
}

.alert-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.4;
  margin-bottom: 8px;
}

.alert-item:last-child {
  margin-bottom: 0;
}

.alert-danger {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.alert-success {
  background: #f0fdf4;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

.alert-close {
  border: none;
  background: transparent;
  font-size: 18px;
  line-height: 1;
  opacity: 0.5;
  padding: 0 0 0 12px;
  min-width: 24px;
  min-height: 24px;
}

/* Body */
.question-list-body {
  flex: 1 1 0;
  overflow-y: auto;
  min-height: 0;
  position: relative;
  -webkit-overflow-scrolling: touch;
  max-width: 640px;
  width: 100%;
  margin: 0 auto;
}

.question-item {
  margin-bottom: 16px;
  position: relative;
}

.question-item:last-child {
  margin-bottom: 0;
}

.question-title {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

/* Multiselect dropdown z-index */
.question-item :deep(.custom-multiselect) {
  position: relative;
}

.question-item:focus-within {
  z-index: 100;
}

.question-item :deep(.multiselect--active) {
  z-index: 101;
}

.question-item :deep(.multiselect__content-wrapper) {
  position: absolute;
  z-index: 102 !important;
}

/* Textarea */
.question-textarea {
  width: 100%;
  min-height: 64px;
  padding: 12px;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  font-size: 16px;
  line-height: 1.5;
  color: #333;
  background: #fff;
  resize: vertical;
  box-sizing: border-box;
}

.question-textarea:focus {
  outline: none;
  border-color: var(--primaryColor, #3e416d);
}

/* Footer */
.question-list-footer {
  flex-shrink: 0;
  display: flex;
  justify-content: flex-end;
  padding: 12px 0 0;
  gap: 8px;
  border-top: 1px solid #e5e7eb;
  margin-top: 16px;
  max-width: 640px;
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}

.btn-primary-action,
.btn-secondary-action {
  min-height: 48px;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}

.btn-primary-action {
  background: var(--primaryColor, #3e416d);
  color: #fff;
}

.btn-secondary-action {
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-primary-action:active,
.btn-secondary-action:active {
  transform: scale(0.98);
  opacity: 0.85;
}

/* Mobile */
@media (max-width: 767px) {
  .question-list-wrapper {
    padding: 12px;
  }

  .btn-primary-action:hover,
  .btn-secondary-action:hover {
    transform: none;
  }
}
</style>
